/**
 * DNA / trait accumulator. Each species carries a profile of trait
 * contributions; consuming the species adds them to the blob's
 * running genome.
 *
 * Categories are strings so new arcs can extend the vocabulary
 * without changing the engine. Consumers (future ability-unlock
 * logic, phenotype renderers, evolution paths) query by category
 * and compare against thresholds.
 *
 * Pure logic. No Phaser dependency. Snapshot/restore friendly.
 */
export type DnaCategory = string;

export type DnaProfile = Readonly<Record<DnaCategory, number>>;

export type DnaListener = (category: DnaCategory, value: number) => void;

export interface DnaSystemOptions {
  initial?: DnaProfile;
}

export class DnaSystem {
  private readonly pool = new Map<DnaCategory, number>();
  private listeners: DnaListener[] = [];

  constructor(options: DnaSystemOptions = {}) {
    if (options.initial) this.contribute(options.initial);
  }

  /** Add a species' DNA profile into the blob's genome. */
  contribute(profile: DnaProfile): void {
    for (const [category, amount] of Object.entries(profile)) {
      if (!Number.isFinite(amount) || amount === 0) continue;
      const next = (this.pool.get(category) ?? 0) + amount;
      this.pool.set(category, next);
      for (const listener of this.listeners) listener(category, next);
    }
  }

  /** Current accumulated value for a category (0 if never added). */
  get(category: DnaCategory): number {
    return this.pool.get(category) ?? 0;
  }

  /** True when accumulated category value meets or exceeds `threshold`. */
  hasTrait(category: DnaCategory, threshold: number): boolean {
    return this.get(category) >= threshold;
  }

  /** All categories present (non-zero), sorted descending by value. */
  dominant(): Array<{ category: DnaCategory; value: number }> {
    return Array.from(this.pool.entries())
      .map(([category, value]) => ({ category, value }))
      .sort((a, b) => b.value - a.value);
  }

  /** All categories as a plain record. */
  all(): Readonly<Record<DnaCategory, number>> {
    return Object.fromEntries(this.pool);
  }

  onChange(listener: DnaListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  snapshot(): Record<DnaCategory, number> {
    return Object.fromEntries(this.pool);
  }

  restore(snap: Record<DnaCategory, number>): void {
    this.pool.clear();
    for (const [k, v] of Object.entries(snap)) {
      if (Number.isFinite(v) && v !== 0) this.pool.set(k, v);
    }
  }
}
